import React, { memo, useState } from "react"
import styled from "styled-components"
import { ErrorMessage, FastField, Field, Formik, useField } from "formik"
import { Form, Button, Row, Col } from "react-bootstrap"
import * as Yup from "yup"

import RMPButton from "../rmp/RMPButton"
import YelpBusinessSearch from "./yelpbusinesssearch"
import RatingField from "../rmp/ratingfield"
import Storage from "@aws-amplify/storage"
import MultiImageUpload from "./upload"
import API from "@aws-amplify/api"
import { createBreakdownRating, createRating } from "../../graphql/mutations"
import { rmpToast, rmpToastError } from "../RMPToasts/rmptoasts"
import RMPErrorBoundary from "../rmp/RMPErrorBoundary"
import { Redirect } from "react-router"
import { gql, useApolloClient } from "@apollo/client"
import { getRatingsByDate } from "../../graphql/queries"

const WarningStyles = styled.div`
    ${({ warn }) => warn && `
        background: #fff8e1;
        border: 2px solid #f9a825;
        border-radius: 12px;
        box-shadow: 0 0 0 4px #ffe082, 0 4px 24px rgba(249,168,37,0.18);
        padding: 16px;
    `}
`

const WarnBanner = styled.div`
    background: #f9a825;
    color: #fff;
    font-weight: bold;
    text-align: center;
    border-radius: 8px;
    padding: 10px 16px;
    margin-bottom: 16px;
    font-size: 1em;
    letter-spacing: 0.03em;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
`

const Styles = styled.div`
    .row {
        margin-bottom: 5px;
    }
    label {
        font-weight: bold;
    }
    textarea {
        width: 100%;
        border: 1px solid #e0e0e0;
        border-radius: 6px;
        padding: 8px 10px;
        margin-top: 6px;
        resize: none;
        font-family: inherit;
        transition: border-color 0.2s;
        :focus {
            outline: none;
            border-color: #aaa;
        }
    }
    .subcategories {
        font-size: 0.9em;
    }
    input {
        width: 100%;
        border-radius: 6px;
        border: 1px solid #ddd;
        padding: 6px 10px;
        font-family: inherit;
        transition: border-color 0.2s;
        :focus {
            outline: none;
            border-color: #aaa;
        }
    }
    input[name=title] {
        background: transparent;
        outline: none;
        border: 0;
        border-bottom: 2px solid #e0e0e0;
        border-radius: 0;
        font-size: 24px;
        padding: 4px 0;
        margin-bottom: 4px;
        :focus {
            border-bottom-color: #aaa;
        }
    }
    .and-or {
        text-align: center;
        font-weight: bold;
        margin: 5px;
        color: #999;
        font-size: 0.85em;
    }
    .error-message {
        font-size: 0.8em;
        color: red;
        text-align: center;
        font-weight: bold;
    }
    .section {
        background: #fafafa;
        border: 1px solid #eeeeee;
        border-radius: 8px;
        padding: 16px 20px;
        margin-bottom: 14px;
    }
    .section-label {
        font-size: 0.72em;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: #aaa;
        margin-bottom: 10px;
    }
    .rating-row {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        padding: 6px 0;
        border-bottom: 1px solid #f0f0f0;
        :last-child {
            border-bottom: 0;
        }
    }
    .rating-row-label {
        width: 140px;
        min-width: 140px;
        font-weight: bold;
        font-size: 0.9em;
        color: #444;
    }
    @media (max-width: 480px) {
        .rating-row {
            flex-direction: column;
            align-items: flex-start;
        }
        .rating-row-label {
            width: 100%;
            margin-bottom: 4px;
        }
    }
`

const SmoothCollapse = styled.div`
    overflow: hidden;
    max-height: ${({ open }) => open ? '120px' : '0'};
    transition: max-height 0.2s ease;
`

const BreakdownRow = memo(function BreakdownRow({ cat }) {
    const [{ value }] = useField(cat)
    return <div>
        <div className='rating-row'>
            <div className='rating-row-label'>{cat}</div>
            <div><FastField name={cat} as={RatingField}/></div>
        </div>
        <SmoothCollapse open={value > 0}>
            <FastField
                name={`${cat}_description`}
                component='textarea'
                rows={2}
                placeholder="Notes..."
            />
        </SmoothCollapse>
    </div>
})
const ratingCategories = {
    Taste: '',
    Presentation: '',
    'Serving Size': '',
    Ambience: '',
    'Strength': ''
}

export default function CreateRatingForm() {
    const client = useApolloClient()
    const [redirect, setRedirect] = useState(null)
    const [imgFiles, setImgFiles] = useState([]) // manage images outside formik
    var schema = Yup.object().shape({
        title: Yup.string(),
        type: Yup.string(),
        overall: Yup.number().min(1, 'Rate your pini!'),
        description: Yup.string(),
        yelpBusinessId: Yup.string().nullable(),
        yelpBusinessName: Yup.string().nullable(),
        restaurantName: Yup.string().nullable(),
    });
    const initialValues = {
        title: '',
        type: 'Rating', // the "primary key" (idk, amplify is weird)
        overall: -1,
        description: '',
        yelpBusinessId: null,
        yelpBusinessName: null,
        restaurantName: '',
        is_pini_adjacent: false,
    }
    Object.keys(ratingCategories).forEach(cat => {
        schema = schema.concat(Yup.object().shape({
            [cat]: Yup.number(),
            [`${cat}_description`]: Yup.string()
        }))
        initialValues[cat] = -1
        initialValues[`${cat}_description`] = ''
    })

    const uploadImg = async (f, uploadName) => {
        if (!uploadName) uploadName = f.name

        console.log(`Uploading ${uploadName}`)
        try {
            const ret = await Storage.put(uploadName, f, {
                contentType: "image/png",
                level: 'public'
            })
            return ret.key
        } catch (error) {
            console.log("Error uploading file: ", error);
        }   
    }

    const getDefaultName = input => {
        if (!input.title) {
            if (!!input.restaurantName && input.restaurantName.length > 0) {
                return `${input.restaurantName} Rating`
            } else if (!!input.yelpBusinessName) {
                return `${input.yelpBusinessName} Rating`
            } else {
                return 'Your Rating'
            }
        } else {
            return input.title
        }
    }

    const handleSubmit = async (values, { setSubmitting }) => {
        console.log('submitting', values)
        const input = { ...values }
        
        if (!input.yelpBusinessId && !input.restaurantName) {
            rmpToastError("You must either search for a bar or supply your own description of this pini's source")
            setSubmitting(false)
            return 
        }

        // Create default name
        input.title = getDefaultName(input)

        // Only submit the sub categories we have ratings for
        const breakdownRatings = []
        Object.keys(ratingCategories).forEach(cat => {
            if (input[cat] != -1) {
                breakdownRatings.push({
                    name: cat,
                    value: input[cat],
                    notes: input[`${cat}_description`]
                })
            }
            delete input[cat]
            delete input[`${cat}_description`]
        })

        // UPLOAD IMAGES
        input['imgKeys'] = []
        for (const i in imgFiles) {
            const f = imgFiles[i]
            const key = await uploadImg(f, `${input.title}-${f.name}-${new Date()}`)
            input['imgKeys'].push(key)
        }

        // First create the rating
        const res = await API.graphql({ 
            query: createRating, 
            variables: { input }
        })
        setSubmitting(false)

        // Then create the breakdown ratings
        if (!!res.errors) {
            console.error('Error creating rating: ', res)
        }
        else if (!!res && !!res.data.createRating) {
            const newRating = res.data.createRating
            const newRatingId = newRating.id
            breakdownRatings.forEach(async function (br) {
                br['ratingID'] = newRatingId 
                console.log('creating', br)
                await API.graphql({ 
                    query: createBreakdownRating, 
                    variables: { input: br }
                }).then(console.log).catch(console.warn)
            })

        } 
        console.log('Finished submitting')
        // can't get this to work for unmounted query
        // const r = await client.refetchQueries({
        //     include: "active",
        //     onQueryUpdated(observableQuery) {
        //         console.log('here', observableQuery)
        //         switch (observableQuery.queryName) {
        //             case 'QueryIWantToRefresh':
        //                 return true;
        //         }
        //         return false;
        //     }
        // })
        setTimeout(() => { window.location.href = '/' }, 1500)
        rmpToast('Success', { autoClose: 1000, hideProgressBar: true })
    }    

    if (!!redirect) return <Redirect to={redirect}/>
    return <Formik
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={handleSubmit}
        validateOnChange={false}
    >
        {({ values, errors, handleSubmit, handleChange, isSubmitting }) => (
        <WarningStyles warn={values.is_pini_adjacent}>
        <Styles>
        <Form onSubmit={handleSubmit}>

            <div style={{marginBottom: '20px'}}>
                <Field name='title'
                    component='input'
                    placeholder='Title your rating'
                />
                <div>
                    <small style={{color: '#aaa', visibility: values.title.length == 0 ? 'visible' : 'hidden'}}>
                        Will be titled: "{getDefaultName(values)}"
                    </small>
                </div>
            </div>

            <div className='section'>
                <div className='section-label'>Where was your pini from?</div>
                <Row noGutters>
                    <Col>
                        <RMPErrorBoundary>
                            <YelpBusinessSearch
                                placeholder='Search for a bar...'
                                setYelpBusinessId={id => handleChange({ target: { value: id, name: 'yelpBusinessId' }})}
                                setYelpBusinessName={name => handleChange({ target: { value: name, name: 'yelpBusinessName' }})}
                            />
                        </RMPErrorBoundary>
                    </Col>
                    <Col sm='auto' className='and-or'>
                        And/Or
                    </Col>
                    <Col>
                        <Field name='restaurantName'
                            component='input'
                            placeholder="Enter a bar's name"
                        />
                    </Col>
                </Row>
            </div>

            <div className='section'>
                <div className='section-label'>Photos</div>
                <MultiImageUpload
                    imgFiles={imgFiles}
                    setImgFiles={setImgFiles}
                />
            </div>

            <div className='section'>
                <div className='section-label'>Overall Rating</div>
                <div className='rating-row'>
                    <div className='rating-row-label'>How was your pini?</div>
                    <div>
                        <Field name='overall' as={RatingField}/>
                        <div className='error-message'>
                            <ErrorMessage name='overall'/>
                        </div>
                    </div>
                </div>
                <SmoothCollapse open={values.overall > 0}>
                    <Field
                        name='description'
                        component='textarea'
                        rows={2}
                        placeholder="Care to elaborate?"
                    />
                </SmoothCollapse>
            </div>

            <div className='section subcategories'>
                <div className='section-label'>Breakdown <em style={{textTransform: 'none', letterSpacing: 0, color: '#bbb'}}>— optional</em></div>
                {Object.keys(ratingCategories).map(cat => <BreakdownRow key={cat} cat={cat}/>)}
            </div>

            <div className='section'>
                <div className='section-label'>Pini Adjacent?</div>
                <label style={{display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontWeight: 'normal'}}>
                    <Field
                        name='is_pini_adjacent'
                        type='checkbox'
                        style={{width: 'auto', accentColor: '#f9a825'}}
                    />
                    <span>This is a <strong>pini adjacent</strong> rating (not a true pini)</span>
                </label>
            </div>

            {values.is_pini_adjacent && <WarnBanner>
                ⚠️ WARNING: THIS IS A PINI ADJACENT RATING ⚠️
            </WarnBanner>}

            <center>
                <RMPButton type="submit">
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                </RMPButton>
            </center>

        </Form>
        </Styles>
        </WarningStyles>
        )}
    </Formik>
}
